import { GridColDef } from '@mui/x-data-grid-premium';
import { useMyAssortmentTranslation } from '@retail/my-assortment/i18n';
import { useMemo } from 'react';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { StackedTextCell } from '@retail/components';
import { PricePerUnit } from '@retail/products/components';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { getLLDate } from '@retail/utils';

interface Props {
  includeVat?: boolean;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 110,
  headerName: '',
  headerAlign: 'center',
  align: 'center',
};

export const usePriceListColumns = ({ includeVat }: Props) => {
  const { t } = useMyAssortmentTranslation();
  const { i18n } = useTranslation();

  return useMemo(() => {
    const MGLSalesCoverageColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'MGLSalesCoverage',
      headerName: t('myAssortment.columns.salesCoverageMGL'),

      renderCell: ({ row }) => {
        const salesPriceCoverage = row.markupOrPriceSuggestionCoverageDegree;

        return salesPriceCoverage
          ? `${formatPrice(Number(salesPriceCoverage.toFixed(2)), i18n.language)}%`
          : '-';
      },
      valueGetter: (_, row) => row.markupOrPriceSuggestionCoverageDegree,
    };

    const MGLSalesPriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'MGLSalesPrice',
      headerName: t('myAssortment.columns.MGLPrice'),

      width: 230,
      renderCell: ({ row }) => {
        const price = includeVat
          ? row.pricesIncVat.markupOrPriceSuggestionPrice
          : row.pricesExVat.markupOrPriceSuggestionPrice;

        return (
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <StackedTextCell
              topLine={
                <PricePerUnit
                  price={price ? formatPrice(price, i18n.language) : '-'}
                  unit={row.primaryUnit}
                />
              }
              nullableBottomLine
              bottomLine={'-'}
            />
          </Box>
        );
      },
      valueGetter: (_, row) => row.pricesExVat.markupOrPriceSuggestionPrice,
    };
    const customerMarkupFactorColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'customerMarkup',
      headerName: t('myAssortment.columns.customerMarkup'),

      renderCell: ({ row }) => {
        const coverageDegree = row.customerMarkupCondition?.coverageDegree;

        return coverageDegree
          ? `${formatPrice(Number(coverageDegree.toFixed(2)), i18n.language)}%`
          : '-';
      },
      valueGetter: (_, row) => row.customerMarkupCondition?.coverageDegree,
    };
    const customerSalesPriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'customerSalesPrice',
      headerName: t('myAssortment.columns.customerPrice'),

      width: 230,
      renderCell: ({ row }) => {
        const price = includeVat ? row.pricesIncVat.salesPrice : row.pricesExVat.salesPrice;
        return (
          <StackedTextCell
            topLine={
              <PricePerUnit
                price={price ? formatPrice(price, i18n.language) : '-'}
                unit={row.primaryUnit}
              />
            }
            nullableBottomLine
            bottomLine={getLLDate(row.salesPriceDate)}
          />
        );
      },
      valueGetter: (_, row) => row.pricesExVat.salesPrice,
    };
    const customerSalesCoverageColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,

      field: 'customerSalesCoverage',
      headerName: t('myAssortment.columns.salesCoverageCustomer'),
      renderCell: ({ row }) => {
        const coverageDegree = row.coverageDegree;

        return coverageDegree
          ? `${formatPrice(Number(coverageDegree.toFixed(2)), i18n.language)}%`
          : '-';
      },
      valueGetter: (_, row) => row.coverageDegree,
    };

    return {
      MGLSalesCoverageColumn,
      MGLSalesPriceColumn,
      customerMarkupFactorColumn,
      customerSalesPriceColumn,
      customerSalesCoverageColumn,
    };
  }, [i18n.language, includeVat, t]);
};
